import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Trophy, Sparkles, MapPin, ArrowRight, Heart, CheckCircle2, XCircle, Phone, Mail, User } from 'lucide-react';
import { subscribeToVerifiedContestants, castVote, Contestant } from '../firebase/dbSchema';
import Modal from '../components/Modal';
import SafeImage from '../components/SafeImage';
import Seo from '../components/Seo';
import { PAGE_METADATA, getVoteStructuredData } from '../seo/pageMetadata';
import { sendToPrivyr } from '../utils/sendToPrivyr';

const Vote = () => {
  const [contestants, setContestants] = useState<Contestant[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedContestant, setSelectedContestant] = useState<Contestant | null>(null);
  const [voting, setVoting] = useState(false);
  const [voteSuccess, setVoteSuccess] = useState(false);
  const [voteError, setVoteError] = useState<string | null>(null);
  const [voterData, setVoterData] = useState({ name: '', phone: '', email: '' });

  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToVerifiedContestants(
      (data) => {
        setContestants(data);
        setLoadError(null);
        setLoading(false);
      },
      () => {
        setLoadError('Unable to load contestants. Please try again later.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const applySuccessfulVote = (contestantId: string) => {
    setContestants((currentContestants) =>
      currentContestants.map((contestant) =>
        contestant.id === contestantId
          ? { ...contestant, votes: (contestant.votes || 0) + 1 }
          : contestant
      )
    );

    setSelectedContestant((currentContestant) =>
      currentContestant?.id === contestantId
        ? { ...currentContestant, votes: (currentContestant.votes || 0) + 1 }
        : currentContestant
    );
  };

  const handleVote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContestant?.id) return;

    setVoting(true);
    setVoteError(null);
    try {
      await castVote(selectedContestant.id, voterData);
      applySuccessfulVote(selectedContestant.id);
      sendToPrivyr({
        name: voterData.name,
        email: voterData.email,
        phone: voterData.phone,
        message: `Vote cast for ${selectedContestant.name}`,
        source: 'Website - Vote',
      });
      setVoteSuccess(true);
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        setVoteError('This vote could not be completed. If this email has already been used, only one vote is allowed.');
      } else if (error?.code === 'contestant-not-verified') {
        setVoteError('This contestant is no longer available for voting.');
      } else {
        setVoteError('Failed to cast vote. Please check your connection and try again.');
      }
    } finally {
      setVoting(false);
    }
  };

  const closeVoteModal = () => {
    setSelectedContestant(null);
    setVoteSuccess(false);
    setVoteError(null);
    setVoterData({ name: '', phone: '', email: '' });
  };

  const getContestantAltText = (contestant: Contestant) =>
    `Portrait of ${contestant.name}, Delight Business Empowerment contestant from ${contestant.residence || contestant.address || 'Nigeria'}`;

  return (
    <>
      <Seo {...PAGE_METADATA.vote} structuredData={getVoteStructuredData()} />
      <div className="pt-32 pb-24 overflow-hidden min-h-screen bg-slate-50">
      <section className="px-6 max-w-7xl mx-auto text-center mb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 bg-royal-accent/50 border border-amber-200 px-4 py-2 rounded-full mb-6">
            <Heart className="w-4 h-4 text-royal-purple fill-royal-purple" />
            <span className="text-royal-purple text-[10px] font-black uppercase tracking-[0.2em]">Public Voting</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-display font-black text-royal-dark tracking-tighter mb-6 break-words">
            Support Your <span className="text-royal-purple">Favorite</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto font-medium">
            Every vote counts. Help these amazing women achieve their dreams and scale their impact.
          </p>
        </motion.div>
      </section>

      <section className="px-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-royal-purple"></div>
          </div>
        ) : loadError ? (
          <div className="text-center py-20 bg-red-50 rounded-[3rem] border border-red-100">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-2xl font-display font-black text-royal-dark">Something went wrong</h3>
            <p className="text-slate-500 mt-2">{loadError}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-6 bg-royal-purple text-white px-8 py-3 rounded-xl font-black"
            >
              Try Again
            </button>
          </div>
        ) : contestants.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-[3rem] shadow-xl border border-slate-100">
            <Sparkles className="w-12 h-12 text-royal-gold mx-auto mb-4" />
            <h3 className="text-2xl font-display font-black text-royal-dark">No Contestants Yet</h3>
            <p className="text-slate-500 mt-2">Check back soon once applications are verified!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {contestants.map((contestant, idx) => (
              <motion.div 
                key={contestant.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-white rounded-[3rem] overflow-hidden shadow-2xl shadow-black/5 border border-slate-100"
              >
                <div className="h-[450px] overflow-hidden relative">
                  <SafeImage 
                    src={contestant.image} 
                    alt={getContestantAltText(contestant)}
                    width={1200}
                    height={1500}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-royal-dark/90 via-royal-dark/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                    <button 
                      onClick={() => setSelectedContestant(contestant)}
                      className="bg-white text-royal-dark px-8 py-4 rounded-2xl font-black text-lg shadow-2xl hover:bg-royal-gold transition-all flex items-center gap-2"
                    >
                      Vote Now <Heart className="w-5 h-5 fill-current" />
                    </button>
                  </div>
                  
                  <div className="absolute top-6 right-6 bg-white/95 backdrop-blur px-5 py-2.5 rounded-2xl font-display font-black text-royal-purple text-sm shadow-xl flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-royal-gold" />
                    {contestant.votes} Votes
                  </div>
                </div>
                <div className="p-10">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-royal-gold font-black text-[10px] uppercase tracking-widest">Contestant</span>
                      <h4 className="text-2xl font-display font-black text-royal-dark mt-1">{contestant.name}</h4>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-slate-500 text-sm font-medium mb-6">
                    <MapPin className="w-4 h-4 mt-0.5 text-royal-purple flex-shrink-0" />
                    <p>
                      <span className="font-black text-royal-dark">State of Residence:</span>{' '}
                      {contestant.residence || contestant.address || 'Not provided'}
                    </p>
                  </div>
                  <button 
                    onClick={() => setSelectedContestant(contestant)}
                    className="w-full bg-royal-accent text-royal-purple py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-royal-purple hover:text-white transition-all flex items-center justify-center gap-2"
                  >
                    Cast Your Vote <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Voting Modal */}
      <Modal
        isOpen={!!selectedContestant}
        onClose={closeVoteModal}
        title={voteSuccess ? "Vote Confirmed!" : `Vote for Miss ${selectedContestant?.name}`}
      >
        <div className="p-2">
          {voteSuccess ? (
            <div className="text-center py-10 space-y-6">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <div className="space-y-2">
                <h3 className="text-3xl font-display font-black text-royal-dark">Thank You!</h3>
                <p className="text-slate-500 font-medium">
                  Your vote for Miss {selectedContestant?.name} has been recorded and the leaderboard has been updated.
                </p>
              </div>
              <button 
                onClick={closeVoteModal}
                className="bg-royal-dark text-white px-10 py-4 rounded-2xl font-black hover:bg-royal-purple transition-all"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleVote} className="space-y-6">
              <div className="flex items-center gap-6 p-6 bg-royal-accent/30 rounded-[2rem] border border-amber-200 mb-8">
                <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-lg">
                  <SafeImage
                    src={selectedContestant?.image}
                    alt={selectedContestant ? getContestantAltText(selectedContestant) : 'Delight Business Empowerment contestant portrait'}
                    width={240}
                    height={240}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="text-xl font-display font-black text-royal-dark">{selectedContestant?.name}</h4>
                  <p className="text-sm text-royal-purple font-black uppercase tracking-widest">
                    {selectedContestant?.residence || selectedContestant?.address || 'State not provided'}
                  </p>
                </div>
              </div>

              {voteError && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm font-medium">
                  {voteError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-black text-royal-dark uppercase tracking-widest mb-2">Your Full Name</label>
                  <input 
                    type="text" 
                    required
                    value={voterData.name}
                    onChange={(e) => setVoterData({...voterData, name: e.target.value})}
                    placeholder="Enter your name"
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all font-medium"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-royal-dark uppercase tracking-widest mb-2">Your Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input 
                      type="tel" 
                      required
                      value={voterData.phone}
                      onChange={(e) => setVoterData({...voterData, phone: e.target.value})}
                      placeholder="Enter your phone number"
                      className="w-full pl-14 pr-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all font-medium"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-black text-royal-dark uppercase tracking-widest mb-2">Your Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input 
                      type="email" 
                      required
                      value={voterData.email}
                      onChange={(e) => setVoterData({...voterData, email: e.target.value})}
                      placeholder="Enter your email"
                      className="w-full pl-14 pr-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all font-medium"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <button 
                  type="submit"
                  disabled={voting}
                  className="w-full bg-royal-dark text-white py-5 rounded-2xl font-black text-lg shadow-2xl hover:bg-royal-purple transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {voting ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <>Confirm Vote <Heart className="w-5 h-5 fill-current" /></>
                  )}
                </button>
                <p className="text-center text-xs text-slate-400 mt-4 font-medium">
                  By voting, you agree to our terms and conditions.
                </p>
              </div>
            </form>
          )}
        </div>
      </Modal>
      </div>
    </>
  );
};

export default Vote;
